/**
 * cost-template.ts — live numbers inside written prose.
 *
 * Why (Trent, 2026-09-06): every stored article that quoted a dollar figure
 * went stale — Dignity's scenario cost analyses quoted medians that are now
 * >1.5× off in 44 of the 48 that cite one, and the Oct-2025 code guides quote
 * 2025 charges by name. The fix is to keep the prose timeless and let the
 * page fill the numbers at render time from the SAME per-code / per-scenario
 * JSON the page, the React app and /api/v1 already read (R2 static cache from
 * scripts/generate_static_cache.py). One source, filled on the way out, so a
 * page can never contradict its own price band.
 *
 * Grammar (double braces, case-insensitive, whitespace allowed):
 *   {{median}}  {{average}}  {{p10}}  {{p90}}  {{min}}  {{max}}
 *   {{hospitals}}                 hospital count, "1,468"
 *   {{spread}}                    p90 ÷ p10 as "23×"
 *   {{variation}}                 priceVariation as "436%"
 *   {{cheapest_hospital}}         "Butler Memorial Hospital in Butler, PA ($40)"
 *   {{priciest_hospital}}         same shape, top of the list
 *   {{cheapest_state}} / {{priciest_state}}   by median of the listed hospitals
 *   {{median|k}} etc.             compact form: $16k, $3.2k, $1.2M
 *   {{setting_median:outpatient}} / {{setting_hospitals:inpatient}}
 * Scenario extras:
 *   {{procedures}}                procedure count
 *   {{top_phase}}                 the phase with the largest median share
 *   {{phase_share:Surgery Day}}   that phase's share of the median bill, "60%"
 *   {{phase_median:Rehabilitation}}
 *
 * Any sentence containing a token that cannot be filled is dropped whole, so
 * a missing stat never leaves "$undefined" or a dangling clause behind. The
 * same module is imported by the React app (src/… → ../../functions/lib/…) so
 * both surfaces render identically.
 */

export interface CostTemplateHospital { n?: string; c?: string; s?: string; p?: number }

export interface CostTemplateContext {
  median?: number | null;
  average?: number | null;
  p10?: number | null;
  p90?: number | null;
  min?: number | null;
  max?: number | null;
  hospitals?: number | null;
  /** pricing.priceVariation (percent) */
  variation?: number | null;
  bySettings?: Array<{ setting: string; medianCharge?: number; avgCharge?: number; hospitalCount?: number }> | null;
  /** hospital rows as in the per-code JSON (n, c, s, p) */
  hospitalRows?: CostTemplateHospital[] | null;
  /** scenario-only */
  procedures?: number | null;
  phaseMedians?: Record<string, number | null> | null;
}

const USD = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

function compact(n: number): string {
  if (n < 1000) return `$${Math.round(n)}`;
  if (n < 10000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  if (n < 1_000_000) return `$${Math.round(n / 1000)}k`;
  return `$${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
}

function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b[a-z]/g, c => c.toUpperCase()).replace(/\bOf\b/g, 'of').replace(/\bAnd\b/g, 'and');
}

function hospitalPhrase(h: CostTemplateHospital | undefined): string | null {
  if (!h || !h.n || h.p == null) return null;
  const where = h.c && h.s ? ` in ${titleCase(h.c)}, ${h.s}` : h.s ? ` in ${h.s}` : '';
  return `${titleCase(h.n)}${where} (${USD(h.p)})`;
}

function stateByMedian(rows: CostTemplateHospital[], pick: 'min' | 'max'): string | null {
  const by: Record<string, number[]> = {};
  for (const r of rows) if (r.s && r.p != null) (by[r.s] ||= []).push(r.p);
  const meds = Object.entries(by).filter(([, v]) => v.length >= 3).map(([s, v]) => {
    const a = v.slice().sort((x, y) => x - y); return [s, a[Math.floor(a.length / 2)]] as const;
  });
  if (!meds.length) return null;
  meds.sort((a, b) => a[1] - b[1]);
  return pick === 'min' ? meds[0][0] : meds[meds.length - 1][0];
}

/** Some scenarios carry snake_case / lower-case phase keys ("initial_workup",
 *  "emergency"); Title-Case them for prose. Keys that already read as titles
 *  ("Inpatient Hospital Stay (DRG Bundle)") pass through untouched. */
function phaseLabel(key: string): string {
  if (/[A-Z]/.test(key)) return key;
  return key.replace(/_/g, ' ').replace(/\b[a-z]/g, c => c.toUpperCase());
}

/** Exact phase key first; case-insensitive only when there is a single match.
 *  A scenario can carry both "follow-up" and "Follow-Up" as separate phases
 *  (phimosis, 2026-09-06) — a case-blind lookup filled the wrong number. */
function findPhase(entries: [string, number][], name: string): [string, number] | undefined {
  const exact = entries.find(([k]) => k === name);
  if (exact) return exact;
  const loose = entries.filter(([k]) => k.toLowerCase() === name.toLowerCase());
  return loose.length === 1 ? loose[0] : undefined;
}

/** Resolve one token to text, or null when the context can't fill it. */
export function resolveToken(raw: string, ctx: CostTemplateContext): string | null {
  const [namePart, fmt] = raw.split('|').map(s => s.trim());
  const [name, argRaw] = namePart.split(':').map(s => s.trim());
  const key = name.toLowerCase();
  const arg = (argRaw || '').toLowerCase();
  const money = (v: number | null | undefined) => (v == null || !isFinite(v) ? null : fmt === 'k' ? compact(v) : USD(v));
  switch (key) {
    case 'median': return money(ctx.median);
    case 'average': case 'mean': return money(ctx.average);
    case 'p10': return money(ctx.p10);
    case 'p90': return money(ctx.p90);
    case 'min': case 'lowest': return money(ctx.min);
    case 'max': case 'highest': return money(ctx.max);
    case 'hospitals': case 'hospital_count': return ctx.hospitals ? ctx.hospitals.toLocaleString('en-US') : null;
    case 'procedures': case 'procedure_count': return ctx.procedures ? String(ctx.procedures) : null;
    case 'spread': return ctx.p10 && ctx.p90 && ctx.p10 > 0 ? `${Math.round(ctx.p90 / ctx.p10)}×` : null;
    case 'variation': return ctx.variation != null ? `${Math.round(ctx.variation)}%` : null;
    case 'cheapest_hospital': {
      // Chargemaster garbage lows ($2 for a CT, $1 for an EKG) would otherwise be
      // "the cheapest hospital" — skip rows under 2% of the median, the same
      // suspicious-price rule the scenario tables use (2026-09-06, batch-1 guides).
      const floor = ctx.median ? ctx.median * 0.02 : 0;
      const rows = (ctx.hospitalRows || []).filter(h => h.p != null && h.p > 0 && h.p >= floor).sort((a, b) => a.p! - b.p!);
      return hospitalPhrase(rows[0]);
    }
    case 'priciest_hospital': {
      // Mirror of the floor: a $17,942 fingerstick or a $129,400 simple wound repair
      // is a unit/mapping error, not a price. Skip rows above 30× the median — a
      // $446k knee replacement against a $16k median (27×) still shows.
      const ceiling = ctx.median ? ctx.median * 30 : Infinity;
      const rows = (ctx.hospitalRows || []).filter(h => h.p != null && h.p > 0 && h.p <= ceiling).sort((a, b) => b.p! - a.p!);
      return hospitalPhrase(rows[0]);
    }
    case 'cheapest_state': return ctx.hospitalRows?.length ? stateByMedian(ctx.hospitalRows, 'min') : null;
    case 'priciest_state': return ctx.hospitalRows?.length ? stateByMedian(ctx.hospitalRows, 'max') : null;
    case 'setting_median': {
      const s = ctx.bySettings?.find(b => (b.setting || '').toLowerCase() === arg);
      return money(s?.medianCharge);
    }
    case 'setting_average': {
      const s = ctx.bySettings?.find(b => (b.setting || '').toLowerCase() === arg);
      return money(s?.avgCharge);
    }
    case 'setting_hospitals': {
      const s = ctx.bySettings?.find(b => (b.setting || '').toLowerCase() === arg);
      return s?.hospitalCount ? s.hospitalCount.toLocaleString('en-US') : null;
    }
    case 'top_phase': {
      const e = Object.entries(ctx.phaseMedians || {}).filter(([, v]) => v != null && v > 0) as [string, number][];
      if (!e.length) return null;
      e.sort((a, b) => b[1] - a[1]);
      return phaseLabel(e[0][0]);
    }
    case 'phase_share': {
      const e = Object.entries(ctx.phaseMedians || {}).filter(([, v]) => v != null && v > 0) as [string, number][];
      const total = e.reduce((s, [, v]) => s + v, 0);
      const hit = findPhase(e, argRaw || '');
      return hit && total > 0 ? `${Math.round((hit[1] / total) * 100)}%` : null;
    }
    case 'phase_median': {
      const e = Object.entries(ctx.phaseMedians || {}).filter(([, v]) => v != null && v > 0) as [string, number][];
      const hit = findPhase(e, argRaw || '');
      return hit ? money(hit[1]) : null;
    }
    default: return null;
  }
}

const TOKEN = /\{\{\s*([^{}]+?)\s*\}\}/g;

/**
 * Fill every {{token}} in `text`. Sentences (or list items / lines) with an
 * unfillable token are removed whole. Returns the filled text; tokens never
 * survive to the reader.
 */
export function fillCostTemplate(text: string, ctx: CostTemplateContext): string {
  if (!text || !text.includes('{{')) return text || '';
  const lines = text.replace(/\r/g, '').split('\n');
  const out: string[] = [];
  for (const line of lines) {
    if (!line.includes('{{')) { out.push(line); continue; }
    // split a prose line into sentences so one bad token drops one sentence,
    // not the paragraph; bullets and headings are single units
    const isUnit = /^\s*([-*•]|\d+[.)]|#{1,6})\s+/.test(line) || !/[.!?]\s/.test(line);
    const parts = isUnit ? [line] : line.match(/[^.!?]+[.!?]+["')\]]?(\s+|$)|[^.!?]+$/g) || [line];
    const kept = parts.map(p => {
      let bad = false;
      const filled = p.replace(TOKEN, (_m, tok: string) => {
        const v = resolveToken(tok, ctx);
        if (v == null) { bad = true; return ''; }
        return v;
      });
      return bad ? null : filled;
    }).filter((p): p is string => p != null);
    const joined = kept.join('').replace(/\s{2,}/g, ' ').trim();
    if (joined) out.push(joined);
  }
  // Indefinite article before a filled number: "a 8× spread" → "an 8× spread".
  // Vowel-sounding numerals are 8, 11, 18 and the 80s; everything else keeps "a".
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim()
    .replace(/\b([Aa]) (\$?)(8\d*|11|18)(?=[×%,.\s])/g, (_m, a, dollar, num) => `${a}n ${dollar}${num}`);
}

/** Context from a per-code JSON (`data.pricing` + `data.hospitals`). */
export function codeContext(data: { pricing?: any; hospitals?: CostTemplateHospital[] } | undefined | null): CostTemplateContext {
  const p = data?.pricing || {};
  return {
    median: p.medianGrossCharge, average: p.avgGrossCharge, p10: p.p10GrossCharge, p90: p.p90GrossCharge,
    min: p.minGrossCharge, max: p.maxGrossCharge, hospitals: p.totalHospitals, variation: p.priceVariation,
    bySettings: p.bySettings || null, hospitalRows: data?.hospitals || null,
  };
}

/** Context from a per-scenario JSON (`data`). */
export function scenarioContext(s: {
  estimatedCost?: number | null; estimatedCostMedian?: number | null; p10Cost?: number | null; p90Cost?: number | null;
  minCost?: number | null; maxCost?: number | null; totalProcedures?: number | null; phaseBreakdownMedian?: Record<string, number | null> | null;
} | undefined | null): CostTemplateContext {
  return {
    median: s?.estimatedCostMedian, average: s?.estimatedCost, p10: s?.p10Cost, p90: s?.p90Cost, min: s?.minCost, max: s?.maxCost,
    procedures: s?.totalProcedures, phaseMedians: s?.phaseBreakdownMedian || null,
  };
}
